/**
 * Dashboard API Service  
 * Handles dashboard statistics and aggregation operations
 */

import apiClient from './api.js';

class DashboardService {
  getDateRange(period = '7d') {
    const now = new Date();
    const endDate = now.toISOString();
    const start = new Date(now);
    if (period === 'today') {
      start.setHours(0, 0, 0, 0);
    } else if (period === 'monthly') {
      start.setDate(start.getDate() - 30);
    } else {
      start.setDate(start.getDate() - 7);
    }
    return { startDate: start.toISOString(), endDate };
  }
  
  /**
   * Get dashboard statistics
   * @returns {Promise<Object>} - Dashboard stats (total scans, phrase/discover counts, etc.)
   */
  async getStats(period = '7d', extraParams = {}) {
    const range = this.getDateRange(period);
    return await apiClient.get('/dashboard/stats', {
      period,
      ...range,
      ...extraParams,
    });
  }

  /**
   * Get detailed dashboard data with breakdown
   * @returns {Promise<Object>} - Enhanced dashboard data
   */
  async getDashboardData(period = '7d') {
    try {
      const response = await this.getStats(period);
      const stats = response.stats || response;
      
      if (response.success === false) {
        throw new Error('Failed to fetch dashboard stats');
      }

      return {
        success: true,
        data: {
          // Scan statistics
          totalScans: stats.totalScans || 0,
          phraseScans: stats.phraseScans || 0,
          discoverScans: stats.discoverScans || 0,
          
          // Group statistics  
          totalGroups: stats.totalGroupsDiscovered || 0,
          fraudulentGroups: stats.totalFraudulent || 0,
          genuineGroups: stats.totalGenuine || 0,
          unmarkedGroups: stats.totalUnmarked || 0,
          
          // Review statistics
          totalReviews: stats.totalReviews || 0,
          
          // Calculated metrics
          fraudRate: this.calculatePercentage(stats.totalFraudulent, stats.totalGroupsDiscovered),
          reviewRate: this.calculatePercentage(
            (stats.totalFraudulent || 0) + (stats.totalGenuine || 0),
            stats.totalGroupsDiscovered
          ),
          
          // Last updated
          lastUpdated: new Date().toISOString()
        }
      };
    } catch (error) {
      console.error('[Dashboard] Error getting dashboard data:', error);
      return {
        success: false,
        error: error.message,
        data: this.getEmptyDashboard()
      };
    }
  }

  /**
   * Get recent activity summary
   * @param {number} days - Number of days to look back (default: 7)
   * @returns {Promise<Object>} - Recent activity data
   */
  async getRecentActivity(days = 7) {
    try {
      // Calculate date threshold
      const since = new Date();
      since.setDate(since.getDate() - days);
      const sinceISO = since.toISOString();

      // Get recent scans and reviews (would need to be added to backend)
      // For now, return empty structure
      return {
        success: true,
        data: {
          period: `Last ${days} days`,
          scansCreated: 0,
          groupsReviewed: 0,
          fraudDetected: 0,
          since: sinceISO
        }
      };
    } catch (error) {
      console.error('[Dashboard] Error getting recent activity:', error);
      return {
        success: false,
        error: error.message,
        data: { period: `Last ${days} days`, scansCreated: 0, groupsReviewed: 0, fraudDetected: 0 }
      };
    }
  }

  /**
   * Get officer performance statistics
   * @param {string} officerId - Officer email
   * @returns {Promise<Object>} - Officer-specific stats
   */
  async getOfficerStats(officerId) {
    try {
      // This would require backend support, simulate for now
      return {
        success: true,
        data: {
          officerId,
          totalScans: 0,
          totalReviews: 0,
          fraudDetected: 0,
          accuracyRate: 0
        }
      };
    } catch (error) {
      console.error('[Dashboard] Error getting officer stats:', error);
      return {
        success: false,
        error: error.message,
        data: { officerId, totalScans: 0, totalReviews: 0, fraudDetected: 0, accuracyRate: 0 }
      };
    }
  }

  /**
   * Refresh dashboard cache (trigger backend to recalculate)
   * @returns {Promise<Object>} - Refresh response
   */
  async refreshDashboard() {
    try {
      // Force fresh data by making a new request
      const stats = await this.getStats();
      
      return {
        success: true,
        message: 'Dashboard refreshed successfully',
        timestamp: new Date().toISOString(),
        data: stats
      };
    } catch (error) {
      console.error('[Dashboard] Error refreshing dashboard:', error);
      return {
        success: false,
        error: error.message,
        timestamp: new Date().toISOString()
      };
    }
  }

  /**
   * Export dashboard data as CSV/JSON
   * @param {string} format - 'csv' or 'json'
   * @returns {Promise<Object>} - Export data
   */
  async exportData(format = 'json') {
    try {
      const dashboardData = await this.getDashboardData();
      
      if (format === 'csv') {
        const csv = this.convertToCSV(dashboardData.data);
        return {
          success: true,
          format: 'csv',
          data: csv,
          filename: `dashboard-export-${new Date().toISOString().split('T')[0]}.csv`
        };
      }
      
      return {
        success: true,
        format: 'json', 
        data: JSON.stringify(dashboardData.data, null, 2),
        filename: `dashboard-export-${new Date().toISOString().split('T')[0]}.json`
      };
    } catch (error) {
      console.error('[Dashboard] Error exporting data:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Helper methods

  /**
   * Calculate percentage safely
   * @param {number} part - Numerator
   * @param {number} total - Denominator  
   * @returns {number} - Percentage (0-100)
   */
  calculatePercentage(part, total) {
    if (!total || total === 0) return 0;
    return Math.round((part / total) * 100 * 100) / 100; // Round to 2 decimals
  }

  /**
   * Get empty dashboard structure
   * @returns {Object} - Empty dashboard data
   */
  getEmptyDashboard() {
    return {
      totalScans: 0,
      phraseScans: 0,
      discoverScans: 0,
      totalGroups: 0,
      fraudulentGroups: 0,
      genuineGroups: 0,
      unmarkedGroups: 0,
      totalReviews: 0,
      fraudRate: 0,
      reviewRate: 0,
      lastUpdated: new Date().toISOString()
    };
  }

  /**
   * Convert dashboard data to CSV format
   * @param {Object} data - Dashboard data
   * @returns {string} - CSV string
   */
  convertToCSV(data) {
    const headers = Object.keys(data).join(',');
    const values = Object.values(data).join(',');
    return `${headers}\n${values}`;
  }

  /**
   * Poll dashboard for real-time updates
   * @param {Function} callback - Callback function to receive updates
   * @param {number} interval - Polling interval in ms (default: 30 seconds)
   * @returns {Function} - Cleanup function to stop polling
   */
  startPolling(callback, period = '7d', interval = 30000) {
    const pollFunction = async () => {
      try {
        const data = await this.getDashboardData(period);
        callback(data);
      } catch (error) {
        console.error('[Dashboard] Polling error:', error);
        callback({ success: false, error: error.message });
      }
    };

    // Initial call
    pollFunction();

    // Set up interval
    const intervalId = setInterval(pollFunction, interval);

    // Return cleanup function
    return () => {
      clearInterval(intervalId);
    };
  }
}

// Create singleton instance
const dashboardService = new DashboardService();

export { dashboardService, DashboardService };
export default dashboardService;
